import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import ParentTemplateFolder from "@/services/template/parent-template-folder";
import type { GetParentTemplateFolder } from "@/dtos/ParentTemplateFolder.dto";

export interface ParentTemplateFoldersParams {
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useParentTemplateFolders(
  params: Ref<ParentTemplateFoldersParams | null> | ParentTemplateFoldersParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["parentTemplateFolders", "disabled"];
    return ["parentTemplateFolders", p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetParentTemplateFolder = {
        organizationId: p.organizationId,
      };

      const result = await ParentTemplateFolder.getParentTemplateFolders(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load parent template folders");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
